import { $getRoot, LexicalEditor as LexicalEditorInstance } from "lexical";

import { LexicalEditor } from "./LexicalEditor";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useEffect, useRef } from "react";
import { $generateNodesFromDOM } from "@lexical/html";
import { EditorProps } from "..";
import usePrevious from "@/shared/hooks/usePrevious";
import { useBoundStore } from "@/app/store";
import { useShallow } from "zustand/shallow";

interface EditorBodyProps {
  initialContent?: string | null;
  isEditable?: boolean;
  isLinkEditMode: boolean;
  note?: EditorProps["note"];
  refetchedNote: EditorProps["refetchedNote"];
  onChange?: (text_content: string | null, html_content: string | null) => void;
  onSetIsLinkEditMode: (isLinkEditMode: boolean) => void;
}

export function EditorContent({
  initialContent,
  isEditable = true,
  isLinkEditMode,
  note,
  refetchedNote,
  onChange,
  onSetIsLinkEditMode,
}: EditorBodyProps) {
  const [editor] = useLexicalComposerContext();
  const initialized = useRef(false);
  const prevRefetchedNote = usePrevious(refetchedNote);
  const deviceType = useBoundStore(useShallow((state) => state.device.type));

  useEffect(() => {
    if (initialContent && !initialized.current) {
      initialized.current = true;
      updateEditorContent(editor, initialContent);
    }

    if (!initialContent && !initialized.current) {
      if (deviceType === "mobile") {
        const input = document.createElement("input");
        input.style.position = "fixed";
        input.style.opacity = "0";
        input.style.height = "0px";
        document.body.appendChild(input);
        input.focus();
        editor.focus();
        input.remove();
      } else {
        // FIXME: temporary fix to focus editor when opened via CMD
        setTimeout(() => {
          editor.focus();
        }, 300);
      }
    }
  }, [deviceType, editor, initialContent]);

  // Effect to refetch note data on tab focus
  useEffect(() => {
    if (
      refetchedNote &&
      prevRefetchedNote?.text_content !== refetchedNote.text_content &&
      refetchedNote.text_content !== note?.text_content
    ) {
      updateEditorContent(editor, refetchedNote.html_content, true);
    }
  }, [editor, prevRefetchedNote, refetchedNote, note]);

  return (
    <LexicalEditor
      classNames={{ content: "pb-8" }}
      isEditable={isEditable}
      isLinkEditMode={isLinkEditMode}
      onChange={onChange}
      onSetIsLinkEditMode={onSetIsLinkEditMode}
    />
  );
}

function updateEditorContent(
  editor: LexicalEditorInstance,
  htmlContent: string | null | undefined,
  selectEnd: boolean = false
) {
  if (htmlContent == null) return; // Do nothing if htmlContent is null or undefined

  editor.update(() => {
    const parser = new DOMParser();
    const dom = parser.parseFromString(htmlContent, "text/html");
    const nodes = $generateNodesFromDOM(editor, dom);

    $getRoot()
      .clear()
      .append(...nodes);

    if (selectEnd) {
      $getRoot().selectEnd();
    }
  });
}
